/* eslint-disable */
<template>
  <!--
    Pull-to-refresh: avatar characters hide BEHIND the content and
    peek over its top edge as you pull down. Faces shake when straining,
    do a playful wave-bounce while loading.
    Stage z-index (1) is BELOW the scroll container (z-index 50 during pull)
    so the content naturally clips / covers the lower portion of each face.
  -->
  <div
    class="ptr-stage"
    :class="{
      'ptr-active': showIndicator,
      'ptr-loading': refreshing,
      'ptr-straining': isStraining
    }"
    :style="stageVars"
    aria-hidden="true"
  >
    <span
      v-for="(a, i) in avatars"
      :key="i"
      class="ptr-face-wrap"
      :class="[`ptr-f${i}`]"
      :style="faceWrapStyle(i)"
    >
      <img :src="a.src" :alt="a.alt" class="ptr-face" />
    </span>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable */
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { haptic } from '@/utils/haptics';

const props = defineProps<{
  enabled?: boolean;
  onRefresh?: () => Promise<void> | void;
}>();

const avatars = [
  { src: '/images/landing/avatar_exec_assistant.png', alt: 'Assistant' },
  { src: '/images/landing/avatar_pm.png', alt: 'PM' },
  { src: '/images/landing/avatar_researcher.png', alt: 'Researcher' },
  { src: '/images/landing/avatar_marketer.png', alt: 'Marketer' }
];

const pulling = ref(false);
const refreshing = ref(false);
const startY = ref(0);
const pullDistance = ref(0);
const thresholdReached = ref(false);
const wasAtTop = ref(false);
const gestureConfirmed = ref(false);
const releasing = ref(false);

const maxPull = 140;
const threshold = 85;
const refreshHold = 64;
const deadZone = 10;

const progress = computed(() => Math.min(pullDistance.value / threshold, 1));

const showIndicator = computed(
  () => pullDistance.value > 2 || refreshing.value
);

const isStraining = computed(
  () => pulling.value && thresholdReached.value && !refreshing.value
);

// 0→1: how far past threshold toward maxPull
const strainLevel = computed(() => {
  if (pullDistance.value <= threshold) return 0;
  return Math.min(1, (pullDistance.value - threshold) / (maxPull - threshold));
});

/* ── Stage tracks the content's top edge ── */
const contentShift = computed(() => Math.min(pullDistance.value, maxPull));

const stageVars = computed(() => ({
  // Stage translates down by the same amount as the content,
  // so it sits exactly at the content's top edge.
  // Faces with negative translateY peek ABOVE the edge;
  // the content (z-index 50) hides everything below the edge.
  transform: `translateY(${contentShift.value}px)`,
  '--strain': strainLevel.value.toString(),
  '--shake-px': `${1 + strainLevel.value * 3.5}px`,
  opacity: showIndicator.value ? 1 : 0,
  top: getStageTop()
}));

/*
  Face positioning — peek from behind the CONTENT edge.
  The stage is at the content's top edge. Faces with translateY < 0
  peek ABOVE the content. Faces at translateY >= 0 are hidden behind
  the content (which has z-index 50 and a solid background).

  peekAmount = how many px above the edge the face center sits.
  At progress 0 → faces fully hidden (Y: +14, behind content).
  At progress 1 → faces peek out (Y: -12, just the tops of heads visible).
*/
const spreadPositions = [
  { x: -48, rot: -8 },    // far left
  { x: -16, rot: -2 },    // centre-left
  { x: 16, rot: 2 },      // centre-right
  { x: 48, rot: 8 }       // far right
];

function faceWrapStyle(index: number) {
  const p = progress.value;
  // Stagger: each face appears one after another
  const stagger = index * 0.14;
  const fp = Math.min(1, Math.max(0, (p - stagger) / (1 - stagger)));
  const spread = spreadPositions[index];

  if (refreshing.value) {
    // During loading: subtly peeking, CSS wave animation takes over
    return {
      transform: `translateX(${spread.x}px) translateY(-16px) scale(1) rotate(${spread.rot}deg)`,
      opacity: 1
    };
  }

  // Ease-out for snappy initial appearance
  const easedFp = 1 - Math.pow(1 - fp, 3);

  // Y: +14 (hidden behind content) → -12 (just barely peeking above content edge)
  // The content clips everything at Y >= 0, so only negative-Y parts are visible
  const y = 14 + (-12 - 14) * easedFp;
  // Scale: start small, grow to full
  const scale = 0.4 + fp * 0.6;
  // Fan out from center
  const xOffset = easedFp * spread.x;
  // Tilt
  const rotate = easedFp * spread.rot;
  // Opacity: quick fade in
  const opacity = Math.min(1, fp * 3);

  return {
    transform: `translateX(${xOffset}px) translateY(${y}px) scale(${scale}) rotate(${rotate}deg)`,
    opacity
  };
}

/* ── Scroll container helpers ── */

function isUserView(): boolean {
  return !!document.querySelector('.mobile-tab-nav');
}

function isCustomersView(): boolean {
  return !!document.querySelector('.customers-table-wrapper');
}

function getStageTop(): string {
  if (window.innerWidth > 1000) return '56px';
  if (isUserView()) return '100px';
  if (isCustomersView()) {
    const wrapper = document.querySelector('.customers-table-wrapper') as HTMLElement;
    if (wrapper) {
      // getBoundingClientRect() includes CSS transforms, but stageVars already
      // applies its own translateY(contentShift). Subtract contentShift to get
      // the element's NATURAL position, avoiding double-shifting the stage.
      const rect = wrapper.getBoundingClientRect();
      const baseTop = rect.top - contentShift.value;
      return `${baseTop}px`;
    }
  }
  return '56px';
}

function getScrollContainer(): HTMLElement | null {
  if (window.innerWidth <= 1000) {
    // On mobile user/customer view, target the visible scrollable area
    const timeline = document.querySelector('.timeline-content') as HTMLElement;
    if (timeline && timeline.offsetParent !== null) return timeline;
    const profileBody = document.querySelector('.user-info .container-body') as HTMLElement;
    if (profileBody && profileBody.offsetParent !== null) return profileBody;
    // On customers page, the table wrapper is the scroll container
    const customersTable = document.querySelector('.customers-table-wrapper') as HTMLElement;
    if (customersTable && customersTable.offsetParent !== null) return customersTable;
  }
  return (
    (document.querySelector('.app-container') as HTMLElement) ??
    (document.querySelector('.users-container') as HTMLElement) ??
    (document.scrollingElement as HTMLElement)
  );
}

function isAtTop(): boolean {
  const container = getScrollContainer();
  if (!container) return true;
  return container.scrollTop <= 1;
}

function isTouchInsideScrolledChild(target: EventTarget | null): boolean {
  let el = target as HTMLElement | null;
  const mainContainer = getScrollContainer();

  while (el && el !== document.body && el !== document.documentElement) {
    if (mainContainer && el === mainContainer) break;
    const style = window.getComputedStyle(el);
    const overflowY = style.overflowY;
    const isScrollable =
      (overflowY === 'auto' || overflowY === 'scroll') &&
      el.scrollHeight > el.clientHeight + 2;
    if (isScrollable && el.scrollTop > 1) return true;
    el = el.parentElement;
  }
  return false;
}

/* ── Push the scroll container down ── */

function applyContentShift(px: number, animate = false) {
  const container = getScrollContainer();
  if (!container) return;
  // Ensure content sits ABOVE the ptr-stage (z:1) so it clips the faces
  container.style.position = 'relative';
  container.style.zIndex = '50';
  if (animate) {
    container.style.transition = 'transform 300ms cubic-bezier(.2,0,0,1)';
  } else {
    container.style.transition = 'none';
  }
  container.style.transform = px > 0 ? `translateY(${px}px)` : '';
}

function clearContentShift(animate = true, smooth = false) {
  const container = getScrollContainer();
  if (!container) return;
  if (animate) {
    const duration = smooth ? '300ms' : '200ms';
    const easing = smooth
      ? 'cubic-bezier(.25,.1,.25,1)'
      : 'cubic-bezier(.2,0,0,1)';
    container.style.transition = `transform ${duration} ${easing}`;
    container.style.transform = 'translateY(0)';
    const onDone = () => {
      container.style.transition = '';
      container.style.transform = '';
      container.style.position = '';
      container.style.zIndex = '';
      container.removeEventListener('transitionend', onDone);
    };
    container.addEventListener('transitionend', onDone, { once: true });
    setTimeout(onDone, smooth ? 350 : 250);
  } else {
    container.style.transition = '';
    container.style.transform = '';
    container.style.position = '';
    container.style.zIndex = '';
  }
}

/* ── Keep content in sync with pullDistance ── */
watch(pullDistance, (d) => {
  if (releasing.value) return;
  applyContentShift(Math.min(d, maxPull), false);
});

/* ── Touch handlers ── */

function onStart(event: TouchEvent) {
  if (!props.enabled || refreshing.value) return;
  if (event.touches.length !== 1) return;

  if (!isAtTop()) {
    wasAtTop.value = false;
    return;
  }
  if (isTouchInsideScrolledChild(event.target)) {
    wasAtTop.value = false;
    return;
  }

  wasAtTop.value = true;
  gestureConfirmed.value = false;
  startY.value = event.touches[0].clientY;
  pullDistance.value = 0;
}

function onMove(event: TouchEvent) {
  if (!props.enabled || refreshing.value) return;
  if (!wasAtTop.value) return;

  const currentY = event.touches[0].clientY;
  const rawDelta = currentY - startY.value;

  if (rawDelta < -5) {
    wasAtTop.value = false;
    pulling.value = false;
    pullDistance.value = 0;
    gestureConfirmed.value = false;
    clearContentShift(false);
    return;
  }

  if (!gestureConfirmed.value && !isAtTop()) {
    wasAtTop.value = false;
    pullDistance.value = 0;
    return;
  }

  if (rawDelta < deadZone) return;

  if (!gestureConfirmed.value) {
    gestureConfirmed.value = true;
    pulling.value = true;
    startY.value = currentY - deadZone;
  }

  if (event.cancelable) event.preventDefault();

  const delta = currentY - startY.value;
  const raw = Math.max(0, delta);

  // ─── Progressive resistance (hyperbolic / asymptotic) ───
  // Feels natural at first but gets dramatically harder the further you pull.
  // Threshold (~85px eased) reached around 240px of finger drag.
  // Past that, each extra inch of drag yields almost nothing — it fights back hard.
  // The pull distance can never actually reach maxPull (it's asymptotic).
  const eased = maxPull * raw / (raw + 160);

  pullDistance.value = Math.min(eased, maxPull);

  if (pullDistance.value >= threshold && !thresholdReached.value) {
    thresholdReached.value = true;
    haptic('medium');
  }
  if (pullDistance.value < threshold && thresholdReached.value) {
    thresholdReached.value = false;
  }
}

async function onEnd() {
  const wasPulling = pulling.value;

  pulling.value = false;
  wasAtTop.value = false;
  gestureConfirmed.value = false;

  if (wasPulling && pullDistance.value >= threshold && props.onRefresh) {
    releasing.value = true;
    applyContentShift(refreshHold, true);
    pullDistance.value = refreshHold;
    releasing.value = false;

    refreshing.value = true;
    try {
      haptic('success');
      await props.onRefresh();
    } finally {
      refreshing.value = false;
      thresholdReached.value = false;
      clearContentShift(true, true);
      // Animate pullDistance down so stage/faces fade out in sync
      const start = pullDistance.value;
      const duration = 300;
      const t0 = performance.now();
      function tick(now: number) {
        const elapsed = now - t0;
        const p = Math.min(elapsed / duration, 1);
        // Match the CSS ease curve
        const eased = 1 - Math.pow(1 - p, 3);
        pullDistance.value = start * (1 - eased);
        if (p < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    }
    return;
  }

  releasing.value = true;
  pullDistance.value = 0;
  thresholdReached.value = false;
  if (wasPulling) {
    clearContentShift(true);
  }
  setTimeout(() => {
    releasing.value = false;
  }, 320);
}

let startHandler: ((e: TouchEvent) => void) | null = null;
let moveHandler: ((e: TouchEvent) => void) | null = null;
let endHandler: (() => void) | null = null;

onMounted(() => {
  startHandler = (e: TouchEvent) => onStart(e);
  moveHandler = (e: TouchEvent) => onMove(e);
  endHandler = () => onEnd();

  window.addEventListener('touchstart', startHandler, { passive: true });
  window.addEventListener('touchmove', moveHandler, { passive: false });
  window.addEventListener('touchend', endHandler, { passive: true });
  window.addEventListener('touchcancel', endHandler, { passive: true });
});

onBeforeUnmount(() => {
  if (startHandler) window.removeEventListener('touchstart', startHandler);
  if (moveHandler) window.removeEventListener('touchmove', moveHandler);
  if (endHandler) {
    window.removeEventListener('touchend', endHandler);
    window.removeEventListener('touchcancel', endHandler);
  }
  clearContentShift(false);
});
</script>

<style scoped>
/*
  The stage sits BELOW the content in z-order (z-index: 1 vs content z-index: 50).
  This means the scroll container's background naturally covers / clips the faces.
  As the content translateY's down, the gap between navbar and content reveals
  the faces — they appear to peek up from behind the content card.
*/
.ptr-stage {
  position: fixed;
  top: 56px;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 0;
  pointer-events: none;
  z-index: 1;
  opacity: 0;
  transition: opacity 80ms ease;
}
.ptr-stage.ptr-active {
  opacity: 1;
}

/* ── Face wrapper (carries position, scale, rotation) ── */
.ptr-face-wrap {
  position: absolute;
  will-change: transform, opacity;
  transition: none;
}

/* ── The actual avatar circle ── */
.ptr-face {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #fff;
  box-shadow:
    0 4px 18px rgba(0, 0, 0, 0.14),
    0 0 0 2.5px #fff;
  object-fit: cover;
  display: block;
}

/* ── Straining: faces shake with increasing intensity ── */
.ptr-straining .ptr-face {
  animation: ptr-shake 0.08s ease-in-out infinite alternate;
}

@keyframes ptr-shake {
  from {
    transform: translateX(calc(var(--shake-px, 1px) * -1)) scaleY(0.96);
  }
  to {
    transform: translateX(var(--shake-px, 1px)) scaleY(0.96);
  }
}

/* ── Loading: staggered bounce-wave while refreshing ── */
.ptr-loading .ptr-face {
  animation: ptr-wave 1.3s cubic-bezier(0.37, 0, 0.63, 1) infinite;
}
.ptr-loading .ptr-f0 .ptr-face { animation-delay: 0s; }
.ptr-loading .ptr-f1 .ptr-face { animation-delay: 0.16s; }
.ptr-loading .ptr-f2 .ptr-face { animation-delay: 0.32s; }
.ptr-loading .ptr-f3 .ptr-face { animation-delay: 0.48s; }

@keyframes ptr-wave {
  0%, 100% {
    transform: translateY(0px) scale(1) rotate(0deg);
  }
  25% {
    transform: translateY(-16px) scale(1.12) rotate(5deg);
  }
  50% {
    transform: translateY(-3px) scale(0.92) rotate(-4deg);
  }
  75% {
    transform: translateY(-8px) scale(1.03) rotate(2deg);
  }
}

/* ── Hide on desktop ── */
@media (min-width: 1001px) {
  .ptr-stage {
    display: none;
  }
}
</style>
